import CommercialLayout from "../../../components/CommercialLayout";
import { useStores } from "../../../context/storesContext";
import {
  BigQueryComparative,
  TurnoverObjective,
} from "../../../types/commercial";
import CommercialContentPage from "../../../components/CommercialContentPage";
import { Range } from "../../../utils/range";
import { fetcherWithStoreKey } from "../../../utils/fetcher";
import useSWR from "swr";
import { EcomServiceType } from "../../../utils/ecomServiceType";
import { useKpiVisibility } from "../../../kpis/useKpiVisibility";
import useBreakPoint from "../../../utils/useBreakPoint";
import CommercialContentPageDesktop from "../../../components/CommercialContentPageDesktop";
import { useAuth } from "../../../Auth/AuthProvider";
import { isEmailInWhitelist } from "../../../utils/whiteListUtils";
import useLandscape from "../../../utils/useLandscape";

function Month() {
  const { user } = useAuth();
  const { selectedStore } = useStores();
  const { isKpiExists } = useKpiVisibility();

  const currentView = useBreakPoint();
  const isLandscape = useLandscape();
  const isDesktopOrTablet =
    currentView === "desktop" ||
    currentView === "tablet" ||
    (currentView === "phone" && isLandscape);
  const displayLegacyUi = !isEmailInWhitelist("ECOMMERCE_NEW_IHM_WHITE_LIST", user) || !isDesktopOrTablet;
  
  // Current Month turnover
  const { data: monthTurnoverData } = useSWR(
    () =>
      selectedStore?.storeKey && isKpiExists("Commercial", "Turnover")
        ? [
            `/commercial/turnover?range=${Range.CurrentMonth}`,
            selectedStore?.storeKey as string, selectedStore.countryKey as string
          ]
        : null,
    ([url, storeEan, countryKey]) =>
      fetcherWithStoreKey(url,  storeEan as string, countryKey as string)
  );
  // past turnover
  const { data: monthPastTurnoverData } = useSWR(
    () =>
      selectedStore?.storeKey && isKpiExists("Commercial", "Turnover")
        ? [
            `/commercial/turnover?range=${Range.CurrentMonthLastYear}`,
            selectedStore?.storeKey as string, selectedStore.countryKey as string
          ]
        : null,
    ([url, storeEan, countryKey]) =>
      fetcherWithStoreKey(url,  storeEan as string, countryKey as string)
  );
  // Month promo turnover
  const { data: monthTurnoverPromoData } = useSWR(
    () =>
      selectedStore?.storeKey && isKpiExists("Commercial", "TurnoverPromo")
        ? [
            `/commercial/turnoverPromo?range=${Range.CurrentMonth}`,
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeKey]) =>
      fetcherWithStoreKey(url,  storeKey as string)
  );

  // Month member card turnover
  const { data: monthMemberCardTurnoverData } = useSWR(
    () =>
      selectedStore?.storeKey && isKpiExists("Commercial", "MemberCardTurnover")
        ? [
            `/commercial/memberCardTurnover?range=${Range.CurrentMonth}&chain=${"SDV"}`,
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeKey]) =>
      fetcherWithStoreKey(url,  storeKey as string)
  );

  // Month past promo turnover
  const { data: monthPastTurnoverPromoData } = useSWR(
    () =>
      selectedStore?.storeKey && isKpiExists("Commercial", "TurnoverPromo")
        ? [
            `/commercial/turnoverPromo?range=${Range.CurrentMonthLastYear}`,
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeKey]) =>
      fetcherWithStoreKey(url,  storeKey as string)
  );

  // Month past member card turnover
  const { data: monthPastMemberCardTurnoverData } = useSWR(
    () =>
      selectedStore?.storeKey && isKpiExists("Commercial", "MemberCardTurnover")
        ? [
            `/commercial/memberCardTurnover?range=${Range.CurrentMonthLastYear}&chain=${"SDV"}`,
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeKey]) =>
      fetcherWithStoreKey(url,  storeKey as string)
  );

  //objective
  const { data: monthObjectiveTurnoverData } = useSWR<TurnoverObjective>(
    () =>
      selectedStore?.storeKey
        ? [
            `/commercial/objective?range=${Range.CurrentMonth}`,
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) =>
      fetcherWithStoreKey(url,  storeEan as string)
  );
  // current Drive turnover
  const { data: monthDriveTurnoverData } = useSWR(
    () =>
      selectedStore?.storeKey
        ? [
            `/drive/turnover?range=${Range.CurrentMonth}&ecomServiceType=${EcomServiceType.EcomTotal}`,
            
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) =>
      fetcherWithStoreKey(url,  storeEan as string)
  );
  // past Drive turnover
  const { data: pastDriveTurnoverData } = useSWR(
    () =>
      selectedStore?.storeKey
        ? [
            `/drive/turnover?range=${Range.CurrentMonthLastYear}&ecomServiceType=${EcomServiceType.EcomTotal}`,
            
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) =>
      fetcherWithStoreKey(url,  storeEan as string)
  );
  //Comparative
  const { data: monthComparativeData, error: monthComparativeError } = useSWR<
    Array<BigQueryComparative>
  >(
    () =>
      selectedStore?.storeKey && isKpiExists("Commercial", "Comparative")
        ? [
            `/commercial/comparative?range=${
              Range.CurrentMonth
            }&storeType=${selectedStore?.storeType?.toUpperCase()}`,
            
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) =>
      fetcherWithStoreKey(url,  storeEan as string)
  );
  // currentMonth sector turnover
  const { data: currentMonthSectorData, error: currentMonthSectorError } =
    useSWR(
      () =>
        selectedStore?.storeKey && isKpiExists("Commercial", "TurnoverBySector") && displayLegacyUi
          ? [
              `/commercial/sector?range=${
                Range.CurrentMonth
              }&storeType=${selectedStore?.storeType.toUpperCase()}&sector=${"SDV"}`,
              
              selectedStore?.storeKey as string,  selectedStore.countryKey as string
            ]
          : null,
      ([url, storeEan, countryKey]) =>
        fetcherWithStoreKey(url,  storeEan as string,  countryKey as string)
    );
  // Objective DRIVE
  const { data: currentDriveObjectiveData } = useSWR(
    () =>
      selectedStore?.storeKey
        ? [
            `/drive/objective?range=${Range.CurrentMonth}&ecomServiceType=${EcomServiceType.Drive}`,
            
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) =>
      fetcherWithStoreKey(url,  storeEan as string)
  );
  // tryptich turnover
    const { data: currentTurnoverTriptychData } = useSWR(
    () =>
      selectedStore?.storeKey && isKpiExists("Commercial", "TurnoverTriptych")
        ? [
            `/commercial/turnoverTriptych/allBrands?range=${Range.CurrentMonth}`,
            
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) =>
      fetcherWithStoreKey(url,  storeEan as string)
  );

    const { data: pastTurnoverTriptychData } = useSWR(
    () =>
      selectedStore?.storeKey && isKpiExists("Commercial", "TurnoverTriptych")
        ? [
            `/commercial/turnoverTriptych/allBrands?range=${Range.CurrentMonthLastYear}`,
            
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) =>
      fetcherWithStoreKey(url,  storeEan as string)
  );

  const contentPageProps = {
    hasTurnoverByHour: false,
    hasComparative: true,
    range: Range.CurrentMonth,
    storeType: selectedStore?.storeType!,
    currentTurnoverPromoData: monthTurnoverPromoData,
    currentMemberCardTurnoverData: monthMemberCardTurnoverData,
    currentDriveData: monthDriveTurnoverData,
    currentTurnoverData: monthTurnoverData,
    driveObjective: currentDriveObjectiveData,
    pastDriveData: pastDriveTurnoverData,
    pastTurnoverData: monthPastTurnoverData,
    pastTurnoverPromoData: monthPastTurnoverPromoData,
    pastMemberCardTurnoverData: monthPastMemberCardTurnoverData,
    sectorListData: currentMonthSectorData,
    turnoverObjectiveData: monthObjectiveTurnoverData!,
    comparativeList: monthComparativeData,
    currentTurnoverTriptychData: currentTurnoverTriptychData,
    pastTurnoverTriptychData: pastTurnoverTriptychData,
    isLoadingSector: !currentMonthSectorData,
    isLoadingTurnover: false,
    isLoadingTurnoverByHour: false,
    isLoadingComparative: !monthComparativeData,
    hasErrorSector:
      !!currentMonthSectorError ||
      currentMonthSectorData === null ||
      !currentMonthSectorData?.[0]?.item?.length,
    hasErrorTurnover: false,
    hasErrorTurnoverByHour: false,
    hasErrorComparative:
      !!monthComparativeError || monthComparativeData === null,
    openInformationModal: () => null,
  };

  return (
    <CommercialLayout>
      {displayLegacyUi ? (
        <CommercialContentPage {...contentPageProps} />
      ) : (
        <CommercialContentPageDesktop {...contentPageProps} />
      )}
    </CommercialLayout>
  );
}

export default Month;
